import React, { useEffect, useMemo, useState } from 'react';
import BaseAutocomplete from '../../form/BaseAutocomplete.jsx';
import BaseInput from '../../form/BaseInput.jsx';
import IconClose from '../../images/IconClose.jsx';
import IconAdd from '../../images/IconAdd.jsx';
import { useDebounce } from '../../../hooks/useDebounce.js';
import { useLazyGetCompaniesQuery } from '../../../api/contact.js';
import { useGetAssociatedOrganizationsTypesQuery } from '../../../api/misc.js';
import BaseDropdown from '../../form/BaseDropdown.jsx';

const ProjectCompanies = ({ isEditMode, companies, setCompanies }) => {
    const [search, setSearch] = useState('');
    const [newCompanyName, setNewCompanyName] = useState('');
    const [isAddingNewCompany, setIsAddingNewCompany] = useState(false);
    const [companiesList, setCompaniesList] = useState([]);
    const debouncedSearch = useDebounce(search, 500);

    const [getCompanies, { isLoading: isLoadingCompanies }] = useLazyGetCompaniesQuery();

    const { data: typesData, isLoading: typesLoading } = useGetAssociatedOrganizationsTypesQuery();

    const types = useMemo(() => {
        return (
            typesData?.response?.types?.map((item) => ({
                label: item.label,
                value: item.value
            })) || []
        );
    }, [typesData]);

    const addCompany = (id, name) => {
        const newCompany = {
            organization_id: id,
            type: '',
            name
        };
        setCompanies((prev) => [...prev, newCompany]);
        setSearch('');
    };

    const updateCompany = (index, type) => {
        const newCompanies = [...companies];
        newCompanies[index].type = type;
        setCompanies(newCompanies);
    };

    const createNewCompany = () => {
        if (!newCompanyName.trim()) return;
        const newCompany = {
            organization_id: null,
            name: newCompanyName.trim()
        };
        setCompanies((prev) => [...prev, newCompany]);
        setNewCompanyName('');
        setIsAddingNewCompany(false);
    };

    const removeCompany = (index) => {
        setCompanies((prev) => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const fetchCompanies = async () => {
            if (debouncedSearch !== '') {
                const result = await getCompanies({ search: debouncedSearch });
                setCompaniesList(result.data?.response?.companies || []);
            } else {
                setCompaniesList([]);
            }
        };

        fetchCompanies();
    }, [debouncedSearch, getCompanies]);

    const handleAddNewCompany = () => {
        setIsAddingNewCompany(true);
        setSearch('');
        setCompaniesList([]);
    };

    const handleCancelAddNew = () => {
        setIsAddingNewCompany(false);
        setNewCompanyName('');
    };

    return (
        <div className='project-companies'>
            <div>
                <div className='flex justify-between items-center mb-1'>
                    <div className='text-theme1-primary2 font-bold text-base'>Companies</div>
                    <button
                        type='button'
                        className='flex items-center justify-center bg-none'
                        onClick={handleAddNewCompany}
                    >
                        <IconAdd className='fill-theme1-title2 w-6 h-6' />
                    </button>
                </div>

                {!isAddingNewCompany ? (
                    <BaseAutocomplete
                        inputClassName='text-theme1-text1 mb-1 rounded-3xl border px-4 py-2 border-theme1-text3 w-full outline-none'
                        items={companiesList.map((company) => ({
                            label: company.name,
                            value: company.id
                        }))}
                        onSelect={(id, label) => addCompany(id, label)}
                        value={search}
                        placeholder='Search for a company...'
                        setValue={setSearch}
                        canAdd
                        onAddNew={handleAddNewCompany}
                        isLoading={isLoadingCompanies}
                        exclude={companies.map((company) => company.organization_id || '')}
                        addText='Add new company'
                    />
                ) : (
                    <div className='flex items-center gap-2'>
                        <BaseInput
                            id='new-company'
                            name='new-company'
                            label=''
                            type='text'
                            value={newCompanyName}
                            setValue={setNewCompanyName}
                            placeholder='Enter company name...'
                            className='flex-grow'
                        />
                        <button
                            type='button'
                            onClick={createNewCompany}
                            className='px-4 py-2 bg-theme1-primary text-white rounded-2xl'
                        >
                            Add
                        </button>
                        <button type='button' onClick={handleCancelAddNew} className='p-0 bg-none text-theme1-title2'>
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            {companies.length > 0 && (
                <div className='flex flex-wrap bg-theme1-primary2 rounded-3xl p-5'>
                    {companies.map((company, index) => (
                        <span
                            key={`${company.name}-${index}`}
                            className={`${index !== companies.length - 1 ? 'mb-4' : ''} flex flex-col sm:flex-row sm:items-center justify-between border border-theme1-text3 bg-theme1-white w-full px-3 py-1 rounded-2xl text-theme1-text1`}
                        >
                            <span className='text-sm mb-1 sm:mb-0 sm:mr-4 truncate'>{company.name}</span>

                            <div className='flex items-center gap-2 w-full sm:w-auto'>
                                <BaseDropdown
                                    name='type'
                                    placeholder='Select Type'
                                    value={company.type}
                                    setValue={(value) => updateCompany(index, value)}
                                    options={types}
                                    disabled={typesLoading}
                                    className='w-full sm:w-auto text-sm'
                                />
                                <button
                                    type='button'
                                    className='flex items-center justify-center p-1 rounded focus:outline-none'
                                    onClick={() => removeCompany(index)}
                                >
                                    <IconClose className='fill-black w-4 h-4' />
                                </button>
                            </div>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProjectCompanies;
